import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import type { ReportTemplate, ReportSection } from './reportTemplates';

interface ExportOptions {
  includeCharts?: boolean;
  includeMetrics?: boolean;
  orientation?: 'portrait' | 'landscape';
}

export function exportToPDF(data: any[], title: string, options: ExportOptions = {}): void {
  const doc = new jsPDF({
    orientation: options.orientation || 'portrait',
    unit: 'pt'
  });

  const pageWidth = doc.internal.pageSize.width;
  const margin = 40;
  
  // Add title
  doc.setFontSize(24);
  doc.text(title, pageWidth / 2, margin, { align: 'center' });
  
  // Add date
  doc.setFontSize(12);
  doc.text(`Generated on: ${format(new Date(), 'PPP')}`, margin, margin * 2);
  
  // Add data tables
  doc.autoTable({
    startY: margin * 3,
    head: [Object.keys(data[0])],
    body: data.map(Object.values),
    margin: { top: margin, right: margin, bottom: margin, left: margin },
    styles: {
      fontSize: 10,
      cellPadding: 5
    },
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250]
    }
  });
  
  // Save the PDF
  const fileName = title.toLowerCase().replace(/\s+/g, '-');
  doc.save(`${fileName}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
}

export function exportToCSV(data: any[], filename: string): void {
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(header => {
      const value = row[header];
      return typeof value === 'string' && value.includes(',') 
        ? `"${value}"`
        : value;
    }).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function generateReport(template: ReportTemplate, data: any): jsPDF {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt'
  });

  const pageWidth = doc.internal.pageSize.width;
  const margin = 40;
  let currentY = margin;

  // Add report title
  doc.setFontSize(24);
  doc.text(template.name, pageWidth / 2, currentY, { align: 'center' });
  currentY += margin;

  // Add generation date
  doc.setFontSize(12);
  doc.text(`Generated on: ${format(new Date(), 'PPP')}`, margin, currentY);
  currentY += margin;

  // Process each section
  template.sections.forEach((section: ReportSection) => {
    // Add section title
    doc.setFontSize(16);
    doc.text(section.title, margin, currentY);
    currentY += margin;

    // Add section content based on type
    switch (section.type) {
      case 'text':
        if (section.content) {
          doc.setFontSize(12);
          const lines = doc.splitTextToSize(section.content, pageWidth - (margin * 2));
          doc.text(lines, margin, currentY);
          currentY += (lines.length * 15) + margin;
        }
        break;

      case 'table':
        if (section.content) {
          doc.autoTable({
            startY: currentY,
            head: [Object.keys(section.content[0])],
            body: section.content.map(Object.values),
            margin: { top: margin, right: margin, bottom: margin, left: margin }
          });
          currentY = (doc as any).lastAutoTable.finalY + margin;
        }
        break;

      case 'metrics':
        if (section.content) {
          Object.entries(section.content).forEach(([key, value]) => {
            doc.setFontSize(12);
            doc.text(`${key}: ${value}`, margin, currentY);
            currentY += 20;
          });
          currentY += margin;
        }
        break;
    }

    // Add page break if needed
    if (currentY > doc.internal.pageSize.height - margin) {
      doc.addPage();
      currentY = margin;
    }
  });

  return doc;
}

export function shareReport(reportUrl: string, recipientEmail?: string): void {
  if (navigator.share) {
    navigator.share({
      title: 'Location Scout Report',
      text: 'Check out this location analysis report',
      url: reportUrl
    }).catch(console.error);
  } else if (recipientEmail) {
    const mailtoLink = `mailto:${recipientEmail}?subject=Location Scout Report&body=View the report at: ${reportUrl}`;
    window.location.href = mailtoLink;
  }
}